import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import Breadcrumb from "@/components/ui/breadcrumb";
import { LeftChevron, Download } from "@/components/icons";
import type {
  Exam,
  ExamAttemptDetailResDto,
  ExamResDtoQuestion,
} from "../types/exam-management.types";
import { examManagementApi } from "../services/exam-management.api";
import styles from "./ExamAttemptResultPage.module.scss";

/** Tek bir cevap + soru seçenekleri birleştirilmiş model */
interface MergedAnswer {
  questionID: string;
  questionText: string;
  isAnswerCorrect: boolean;
  /** Boş mu (kullanıcı hiç seçenek seçmemiş) */
  isEmpty: boolean;
  allOptions: {
    text: string;
    isCorrect: boolean;
    isSelected: boolean;
  }[];
}

const ExamAttemptResultPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { examId, attemptId } = useParams<{
    examId: string;
    attemptId: string;
  }>();

  const [exam, setExam] = useState<Exam | null>(null);
  const [attemptData, setAttemptData] =
    useState<ExamAttemptDetailResDto | null>(null);
  const [examQuestions, setExamQuestions] = useState<ExamResDtoQuestion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch exam info + attempt answers + exam detail (for options)
  const fetchData = useCallback(async () => {
    if (!examId || !attemptId) return;
    setIsLoading(true);
    setError(null);
    try {
      const [examInfo, attempt, examDetail] = await Promise.all([
        examManagementApi.getById(examId),
        examManagementApi.getAttemptAnswers(attemptId),
        examManagementApi.getRaw(examId),
      ]);
      setExam(examInfo);
      setAttemptData(attempt);
      setExamQuestions(examDetail.data.questions ?? []);
    } catch (err) {
      console.error("Error fetching attempt result:", err);
      setError(
        err instanceof Error ? err.message : t("examAttemptResult.loadFailed"),
      );
    } finally {
      setIsLoading(false);
    }
  }, [examId, attemptId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const attendeeName = useMemo(() => {
    if (!attemptData?.user) return "";
    const { firstName, lastName } = attemptData.user;
    return [firstName, lastName].filter(Boolean).join(" ") || t("examAttemptResult.participantFallback");
  }, [attemptData]);

  /** Cevapları exam soru seçenekleri ile birleştir */
  const mergedAnswers: MergedAnswer[] = useMemo(() => {
    if (!attemptData?.answers) return [];

    return attemptData.answers.map((answer) => {
      // Exam'daki soruyu bul (tüm seçenekleri almak için)
      const examQuestion = examQuestions.find(
        (q) => q.id === answer.questionID,
      );

      // selectedOptions / correctOptions string, ID veya obje olabilir
      const extractValues = (v: unknown): string[] => {
        if (typeof v === "string") return [v.toLowerCase().trim()];
        if (v && typeof v === "object") {
          const obj = v as Record<string, unknown>;
          const vals: string[] = [];
          if (typeof obj.id === "string") vals.push(obj.id.toLowerCase().trim());
          if (typeof obj.optionText === "string") vals.push(obj.optionText.toLowerCase().trim());
          if (typeof obj.text === "string") vals.push(obj.text.toLowerCase().trim());
          return vals.length > 0 ? vals : [String(v).toLowerCase().trim()];
        }
        return [String(v ?? "").toLowerCase().trim()];
      };

      const selectedValues = (answer.selectedOptions ?? []).flatMap(extractValues);
      const correctValues = (answer.correctOptions ?? []).flatMap(extractValues);
      const selectedSet = new Set(selectedValues);
      const correctSet = new Set(correctValues);

      const isEmpty = selectedSet.size === 0;

      let allOptions: MergedAnswer["allOptions"];

      if (examQuestion?.options?.length) {
        // Exam'daki seçenekleri kullan — hem ID hem text ile eşleştir
        allOptions = examQuestion.options
          .sort((a, b) => a.order - b.order)
          .map((opt) => {
            const optId = opt.id.toLowerCase().trim();
            const optText = opt.optionText.toLowerCase().trim();
            return {
              text: opt.optionText,
              isCorrect:
                opt.isCorrect ||
                correctSet.has(optId) ||
                correctSet.has(optText),
              isSelected:
                selectedSet.has(optId) || selectedSet.has(optText),
            };
          });
      } else {
        // Exam sorular erişilemezse, sadece selected ve correct'lerden oluştur
        const allTexts = new Set<string>();
        (answer.selectedOptions ?? []).forEach((s) => {
          const display = typeof s === "string"
            ? s
            : (s && typeof s === "object"
              ? ((s as Record<string, unknown>).optionText as string) ??
                ((s as Record<string, unknown>).text as string) ??
                ((s as Record<string, unknown>).id as string) ??
                String(s)
              : String(s ?? ""));
          allTexts.add(display);
        });
        (answer.correctOptions ?? []).forEach((s) => {
          const display = typeof s === "string"
            ? s
            : (s && typeof s === "object"
              ? ((s as Record<string, unknown>).optionText as string) ??
                ((s as Record<string, unknown>).text as string) ??
                ((s as Record<string, unknown>).id as string) ??
                String(s)
              : String(s ?? ""));
          allTexts.add(display);
        });
        allOptions = Array.from(allTexts).map((text) => ({
          text,
          isCorrect: correctSet.has(text.toLowerCase().trim()),
          isSelected: selectedSet.has(text.toLowerCase().trim()),
        }));
      }

      return {
        questionID: answer.questionID,
        questionText: answer.questionText,
        isAnswerCorrect: answer.isAnswerCorrect,
        isEmpty,
        allOptions,
      };
    });
  }, [attemptData, examQuestions]);

  // Stats hesapla
  const statsData = useMemo(() => {
    const answers = attemptData?.answers ?? [];
    const score = attemptData?.score ?? 0;
    let correctCount = 0;
    let wrongCount = 0;
    let emptyCount = 0;

    answers.forEach((a) => {
      const hasSelection =
        a.selectedOptions && a.selectedOptions.length > 0;
      if (!hasSelection) {
        emptyCount++;
      } else if (a.isAnswerCorrect) {
        correctCount++;
      } else {
        wrongCount++;
      }
    });

    return { score, correctCount, wrongCount, emptyCount };
  }, [attemptData]);

  const handleDownload = () => {
    if (!attemptData || !exam) return;
    const rows: string[][] = [
      [t("examAttemptResult.csvHeaders.question"), t("examAttemptResult.csvHeaders.selectedAnswer"), t("examAttemptResult.csvHeaders.correctAnswer"), t("examAttemptResult.csvHeaders.result")],
    ];
    mergedAnswers.forEach((a) => {
      const selected = a.allOptions
        .filter((o) => o.isSelected)
        .map((o) => o.text)
        .join(", ");
      const correct = a.allOptions
        .filter((o) => o.isCorrect)
        .map((o) => o.text)
        .join(", ");
      const result = a.isEmpty
        ? t("examAttemptResult.empty")
        : a.isAnswerCorrect
          ? t("examAttemptResult.correct")
          : t("examAttemptResult.wrong");
      rows.push([a.questionText, selected || "-", correct, result]);
    });
    const BOM = "\uFEFF";
    const csv =
      BOM +
      rows
        .map((r) =>
          r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";"),
        )
        .join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sinav-sonuc-${attendeeName || attemptId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const breadcrumbItems = [
    {
      label: t("examManagement.title"),
      href: "/dashboard/exam-management",
    },
    {
      label: t("examManagement.attendees.breadcrumb"),
      href: `/dashboard/exam-management/${examId}/attendees`,
    },
    {
      label: t("examManagement.attemptResult.breadcrumb"),
    },
  ];

  const getQuestionState = (
    answer: MergedAnswer,
  ): "correct" | "wrong" | "empty" => {
    if (answer.isEmpty) return "empty";
    return answer.isAnswerCorrect ? "correct" : "wrong";
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <Header title={t("examManagement.title")} />
        <div className={styles.loading}>
          {t("common.loading")}
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Header title={t("examManagement.title")} />
        <div className={styles.errorState}>{error}</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Header title={t("examManagement.title")} />

      {/* Breadcrumb */}
      <div className={styles.breadcrumbContainer}>
        <button
          type="button"
          className={styles.backButton}
          onClick={() =>
            navigate(`/dashboard/exam-management/${examId}/attendees`)
          }
          aria-label={t("common.back")}
        >
          <LeftChevron width={20} height={20} />
        </button>
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Title Row */}
      <div className={styles.titleRow}>
        <h2 className={styles.examTitle}>{exam?.name ?? ""}</h2>
        {attendeeName && (
          <span className={styles.attendeeBadge}>{attendeeName}</span>
        )}
        <button
          type="button"
          className={styles.downloadBtn}
          onClick={handleDownload}
          aria-label={t("common.download")}
        >
          <Download width={20} height={20} />
        </button>
      </div>

      {/* Stats Cards */}
      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <div className={styles.statHeader}>
            <span className={styles.statLabel}>
              {t("examManagement.attemptResult.score")}
            </span>
            <div className={`${styles.statIcon} ${styles.statIconBlue}`}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <rect
                  x="2"
                  y="3"
                  width="16"
                  height="14"
                  rx="2"
                  stroke="#3B82F6"
                  strokeWidth="1.5"
                />
                <path
                  d="M6 8h8M6 12h5"
                  stroke="#3B82F6"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>
          <span className={styles.statValue}>{statsData.score}</span>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statHeader}>
            <span className={styles.statLabel}>
              {t("examManagement.attemptResult.correct")}
            </span>
            <div className={`${styles.statIcon} ${styles.statIconGreen}`}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle
                  cx="10"
                  cy="10"
                  r="8"
                  stroke="#22C55E"
                  strokeWidth="1.5"
                />
                <path
                  d="M6.5 10l2.5 2.5 5-5"
                  stroke="#22C55E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
          <span className={styles.statValue}>{statsData.correctCount}</span>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statHeader}>
            <span className={styles.statLabel}>
              {t("examManagement.attemptResult.wrong")}
            </span>
            <div className={`${styles.statIcon} ${styles.statIconRed}`}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle
                  cx="10"
                  cy="10"
                  r="8"
                  stroke="#E7212B"
                  strokeWidth="1.5"
                />
                <path
                  d="M7 7l6 6M13 7l-6 6"
                  stroke="#E7212B"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>
          <span className={styles.statValue}>{statsData.wrongCount}</span>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statHeader}>
            <span className={styles.statLabel}>
              {t("examManagement.attemptResult.empty")}
            </span>
            <div className={`${styles.statIcon} ${styles.statIconGray}`}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle
                  cx="10"
                  cy="10"
                  r="8"
                  stroke="#9CA3AF"
                  strokeWidth="1.5"
                />
              </svg>
            </div>
          </div>
          <span className={styles.statValue}>{statsData.emptyCount}</span>
        </div>
      </div>

      {/* Questions */}
      <div className={styles.questionsSection}>
        {mergedAnswers.map((answer, index) => {
          const state = getQuestionState(answer);
          return (
            <div
              key={answer.questionID}
              className={`${styles.questionCard} ${styles[state]}`}
            >
              <p className={`${styles.questionNumber} ${styles[state]}`}>
                {index + 1}.{" "}
                {t("examManagement.attemptResult.question")}
              </p>
              <p className={styles.questionText}>{answer.questionText}</p>
              <div className={styles.optionsList}>
                {answer.allOptions.map((opt, optIdx) => {
                  // 4 durum:
                  // 1) Seçildi + Doğru  → dolu yeşil ✓
                  // 2) Seçilmedi + Doğru → çerçeveli yeşil ✓ (kaçırılmış doğru)
                  // 3) Seçildi + Yanlış → dolu kırmızı ✗
                  // 4) Seçilmedi + Yanlış → ikon yok
                  const selectedAndCorrect = opt.isSelected && opt.isCorrect;
                  const missedCorrect = !opt.isSelected && opt.isCorrect;
                  const selectedAndWrong = opt.isSelected && !opt.isCorrect;

                  let iconClass = styles.emptyIcon;
                  if (selectedAndCorrect) iconClass = styles.correctSelectedIcon;
                  else if (missedCorrect) iconClass = styles.correctMissedIcon;
                  else if (selectedAndWrong) iconClass = styles.wrongIcon;

                  return (
                    <div
                      key={optIdx}
                      className={`${styles.optionItem} ${
                        selectedAndCorrect
                          ? styles.optionCorrectSelected
                          : missedCorrect
                            ? styles.optionCorrectMissed
                            : selectedAndWrong
                              ? styles.optionWrongSelected
                              : ""
                      }`}
                    >
                      <span className={`${styles.optionIcon} ${iconClass}`}>
                        {selectedAndCorrect && (
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M2 7l3.5 3.5L12 4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                        {missedCorrect && (
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M2 7l3.5 3.5L12 4" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                        {selectedAndWrong && (
                          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M3 3l8 8M11 3l-8 8" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                          </svg>
                        )}
                      </span>
                      <span className={styles.optionText}>{opt.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </DashboardLayout>
  );
};

export default ExamAttemptResultPage;
